import { mutate } from 'swr';
import useStores from '@stores/useStores';
import { ApiGetResource, deleteAccountApi } from '@api';
import { getResourceCacheKey } from './useLoadAccountsIntoAccountManagementStore';

export const useDeleteAccounts = () => {
  const { workspaceStore, messagesStore } = useStores();

  return async (accountId: string, resourceDefinitionName: string) => {
    try {
      await deleteAccountApi(workspaceStore.id, accountId);
    } catch (error) {
      messagesStore.showErrorMessage(error as string);
      return false;
    }

    // The account is deleted from here on, so nothing below may report a delete
    // failure. `mutate` revalidates through the accounts fetcher and `fetchData`
    // rethrows on a failed GET, so awaiting it inside the `try` above would report
    // "Failed to delete" for an account that is already gone — and every caller
    // *branches* on the returned boolean, so the misreport is acted on rather than
    // merely displayed: `handleDisconnectDelete` would skip `onDisconnected()` and
    // leave the storage card Connected against a deleted account, and
    // `DeleteAccountConfirmationModalStep` would strand the user on a confirmation for
    // a row that no longer exists. Either way the retry 404s into a second error toast.
    //
    // The refresh is a cache side effect, not part of the delete's success: the
    // fetcher's own `onError` already surfaces the failed GET, and the list revalidates
    // on focus. try/catch rather than a `.catch()` chain so the guard holds for any
    // awaitable `mutate` returns.
    try {
      await mutate(
        getResourceCacheKey(workspaceStore.id, resourceDefinitionName, ApiGetResource.ACCOUNTS),
      );
    } catch {
      // Deliberately swallowed — see above.
    }

    return true;
  };
};
